import React, { useEffect, useState, useMemo, useCallback } from "react";
import moment from "moment";
import { 
  Box, 
  Button, 
  Checkbox, 
  Grid, 
  IconButton, 
  Paper, 
  Stack, 
  Typography,
  FormControlLabel,
  Tooltip,
  Popover,
  Autocomplete,
  TextField,
  Chip
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import GenericModal from "components/GenericModal";
import { Cancel, Save as SaveIcon, ChevronLeft, ChevronRight } from "@mui/icons-material";

const WEEKDAYS_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const WeeklyScheduleConfigurationModal = ({
  selectedShiftIndex,
  getValues,
  setValue,
  isWeeklyScheduleModalOpen,
  setWeeklyScheduleModalOpen,
  saveChanges,
}) => {
  const defaultSchedule = getValues(`shifts[${selectedShiftIndex}].defaultSchedule`) || {};
  const shiftRoster = getValues(`shifts[${selectedShiftIndex}].shiftRoster`) || [];
  const shiftStartDate = getValues(`shifts[${selectedShiftIndex}].shiftStartDate`);
  const shiftEndDate = getValues(`shifts[${selectedShiftIndex}].shiftEndDate`);

  // State for current week and assign to all week
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  const [assignToAllWeek, setAssignToAllWeek] = useState(false);

  // Position assignment state
  const [positionPopupAnchor, setPositionPopupAnchor] = useState(null);
  const [selectedEmployeeForPosition, setSelectedEmployeeForPosition] = useState(null);
  const [selectedDateForPosition, setSelectedDateForPosition] = useState(null);
  const [selectedDayForPosition, setSelectedDayForPosition] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState("");
  const [positionError, setPositionError] = useState("");

  // Filter valid schedule entries - memoized
  const validDays = useMemo(() => {
    return Object.entries(defaultSchedule)
      .filter(([, { start, end }]) => start && end)
      .sort(([dayA], [dayB]) => WEEKDAYS_ORDER.indexOf(dayA) - WEEKDAYS_ORDER.indexOf(dayB));
  }, [defaultSchedule]);

  // State for rows
  const [rows, setRows] = useState([]);

  // Initialize current week start date when modal opens
  useEffect(() => {
    if (shiftStartDate && isWeeklyScheduleModalOpen) {
      const startDate = new Date(shiftStartDate);
      // Set to start of week (Sunday)
      const dayOfWeek = startDate.getDay();
      const startOfWeek = new Date(startDate);
      startOfWeek.setDate(startDate.getDate() - dayOfWeek);
      setCurrentWeekStart(startOfWeek);
    }
  }, [shiftStartDate, isWeeklyScheduleModalOpen]);

  // Reset states when modal opens
  useEffect(() => {
    if (isWeeklyScheduleModalOpen) {
      setAssignToAllWeek(false);
    }
  }, [isWeeklyScheduleModalOpen]);

  // Get week dates for current week (only within shift range) - memoized

// Replace the getWeekDates function with this:
const getWeekDates = useCallback(() => {    
  const weekDates = [];
  const startOfWeek = new Date(currentWeekStart);
  
  for (let i = 0; i < 7; i += 1) {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    
    // Only include dates within shift range
    if (shiftStartDate && shiftEndDate) {
      const dateMoment = moment(date);
      const shiftStart = moment(shiftStartDate);
      const shiftEnd = moment(shiftEndDate);
      
      if (dateMoment.isBetween(shiftStart, shiftEnd, 'day', '[]')) {
        weekDates.push(date);
      }
    } else {
      weekDates.push(date);
    }
  }
  
  return weekDates;
}, [currentWeekStart, shiftStartDate, shiftEndDate]);


  // Memoized week dates
  const weekDates = useMemo(() => getWeekDates(), [getWeekDates]);

  
  useEffect(() => {    
    if (isWeeklyScheduleModalOpen && shiftRoster.length > 0 && validDays.length > 0) {
      const newRows = shiftRoster.map((employee) => {
        const rowData = { id: employee._id, fullName: employee.fullName };
        
        // For each valid day in the schedule
        validDays.forEach(([dayName]) => {
          // Find the date in current week that matches this day
          const dayDate = weekDates.find(date => 
            moment(date).format("dddd").toLowerCase() === dayName
          );
          
          if (dayDate) {
            const dateKey = moment(dayDate).format("YYYY-MM-DD");
            const roster = getValues(`shifts[${selectedShiftIndex}].defaultSchedule.${dayName}.roster`) || [];
            
            let isAssigned = false;
            if (Array.isArray(roster) && roster.length > 0) {
              // Check if this is the new format with objects containing employeeId and date
              if (typeof roster[0] === 'object' && 'employeeId' in roster[0] && 'date' in roster[0]) {
                isAssigned = roster.some(item => 
                  item.employeeId === employee._id && item.date === dateKey
                );
              }
            }
            
            rowData[`${dayName}_${dateKey}`] = isAssigned;
            console.log(`Employee ${employee.fullName} - ${dayName} ${dateKey}: ${isAssigned}`);
          }
        });
        
        return rowData;
      });
      
      console.log('Setting new rows:', newRows);
      setRows(newRows);
    } else if (!isWeeklyScheduleModalOpen) {
      // Clear rows when modal closes
      setRows([]);
    }
  }, [
    isWeeklyScheduleModalOpen, 
    shiftRoster, 
    validDays, 
    weekDates, 
    selectedShiftIndex, 
    getValues, 
    currentWeekStart
  ]);


  // Check if a specific day is available in the current week - memoized
  const isDayAvailable = useCallback((dayName) => {
    return weekDates.some(date => moment(date).format("dddd").toLowerCase() === dayName);
  }, [weekDates]);

  // Checkbox handler - optimized
  const handleCheckboxChange = useCallback((day, date, userId, isChecked) => {
    if (!date) return; // Don't process if no date is available
    const dateKey = moment(date).format("YYYY-MM-DD");
    const dayName = moment(date).format("dddd").toLowerCase();
    const roster = getValues(`shifts[${selectedShiftIndex}].defaultSchedule.${dayName}.roster`) || [];
    let updatedRoster = Array.isArray(roster) ? [...roster] : [];
    if (assignToAllWeek) {
      if (shiftStartDate && shiftEndDate) {
        const start = moment(shiftStartDate);
        const end = moment(shiftEndDate);
        for (let m = moment(start); m.isSameOrBefore(end, "day"); m.add(1, "days")) {
          if (m.format("dddd").toLowerCase() === dayName) {
            const weekDateKey = m.format("YYYY-MM-DD");
            if (isChecked) {
              const existingIndex = updatedRoster.findIndex(item => item.employeeId === userId && item.date === weekDateKey);
              if (existingIndex === -1) {
                updatedRoster.push({ employeeId: userId, date: weekDateKey });
              }
            } else {
              updatedRoster = updatedRoster.filter(item => !(item.employeeId === userId && item.date === weekDateKey));
            }
          }
        }
      }
    } else if (isChecked) {
      const existingIndex = updatedRoster.findIndex(item => item.employeeId === userId && item.date === dateKey);
      if (existingIndex === -1) {
        updatedRoster.push({ employeeId: userId, date: dateKey });
      }
    } else {
      updatedRoster = updatedRoster.filter(item => !(item.employeeId === userId && item.date === dateKey));
    }
    setValue(`shifts[${selectedShiftIndex}].defaultSchedule.${dayName}.roster`, updatedRoster, { shouldDirty: true });
    setRows((prevRows) => {
      const newRows = [...prevRows];
      const rowIndex = newRows.findIndex(row => row.id === userId);
      if (rowIndex !== -1) {
        if (assignToAllWeek) {
          if (shiftStartDate && shiftEndDate) {
            const start = moment(shiftStartDate);
            const end = moment(shiftEndDate);
            for (let m = moment(start); m.isSameOrBefore(end, "day"); m.add(1, "days")) {
              if (m.format("dddd").toLowerCase() === dayName) {
                const weekDateKey = m.format("YYYY-MM-DD");
                newRows[rowIndex][`${dayName}_${weekDateKey}`] = isChecked;
              }
            }
          }
        } else {
          newRows[rowIndex][`${dayName}_${dateKey}`] = isChecked;
        }
      }
      return newRows;
    });
  }, [assignToAllWeek, shiftStartDate, shiftEndDate, selectedShiftIndex, getValues, setValue]);

  // Helper to capitalize day names
  const capitalizeFirstLetter = (string) => string.charAt(0).toUpperCase() + string.slice(1);

  // Position handling functions
  const handlePositionButtonClick = (event, employee, date, dayName) => {
    console.log("handlePositionButtonClick called:", { employee, date, dayName });
    setPositionPopupAnchor(event.currentTarget);
    setSelectedEmployeeForPosition(employee);
    setSelectedDateForPosition(date);
    setSelectedDayForPosition(dayName);
    setSelectedPosition("");
  };

  const handlePositionClose = () => {
    setPositionPopupAnchor(null);
    setSelectedEmployeeForPosition(null);
    setSelectedDateForPosition(null);
    setSelectedDayForPosition(null);
    setSelectedPosition("");
    setPositionError("");
  };

  // Get positions for the current shift
  const getShiftPositions = () => {
    const shiftPositions = getValues(`shifts[${selectedShiftIndex}].positions`);
    return shiftPositions || [];
  };

  // Get the current assigned position for the employee/date/day
  const getEmployeePositionForDay = (employeeId, date, dayName) => {
    if (!date || !dayName) return "";
    const dateKey = moment(date).format("YYYY-MM-DD");
    const roster = getValues(`shifts[${selectedShiftIndex}].defaultSchedule.${dayName}.roster`) || [];
    
    if (Array.isArray(roster) && roster.length > 0) {
      const entry = roster.find(
        (item) => item.employeeId === employeeId && item.date === dateKey
      );
      return entry?.assignedPosition || "";
    }
    return "";
  };

  // Helper function to get position availability for a specific date
  const getPositionAvailabilityForDate = (positionName, date, dayName) => {
    const shiftPositions = getShiftPositions();
    if (!shiftPositions || shiftPositions.length === 0) return { total: 0, assigned: 0, available: 0 };

    const position = shiftPositions.find((pos) => pos.positionName === positionName);
    if (!position) return { total: 0, assigned: 0, available: 0 };

    const total = parseInt(position.numberPositions, 10) || 0;
    const dateKey = moment(date).format("YYYY-MM-DD");
    const roster = getValues(`shifts[${selectedShiftIndex}].defaultSchedule.${dayName}.roster`) || [];
    
    let assigned = 0;
    if (Array.isArray(roster) && roster.length > 0) {
      // Count only employees who are actually assigned to this position on this specific date
      assigned = roster.filter(
        (item) => item.date === dateKey && 
                   item.assignedPosition === positionName &&
                   item.employeeId // Make sure employeeId exists
      ).length;
    }

    const available = Math.max(0, total - assigned);
    return { total, assigned, available };
  };

  // Helper function to check for position conflicts on a specific day
  const checkPositionConflict = (positionName, date, dayName, employeeId) => {
    const dateKey = moment(date).format("YYYY-MM-DD");
    const roster = getValues(`shifts[${selectedShiftIndex}].defaultSchedule.${dayName}.roster`) || [];
    
    if (!Array.isArray(roster) || roster.length === 0) return { hasConflict: false, conflictingEmployee: null };
    
    // Get position details to check availability
    const shiftPositions = getShiftPositions();
    const position = shiftPositions?.find((pos) => pos.positionName === positionName);
    if (!position) return { hasConflict: false, conflictingEmployee: null };
    
    const totalPositions = parseInt(position.numberPositions, 10) || 0;
    
    // Count how many employees are currently assigned to this position on this date
    const currentAssignments = roster.filter(
      (item) => item.date === dateKey && 
                 item.assignedPosition === positionName &&
                 item.employeeId // Make sure employeeId exists
    );
    
    const currentAssignmentCount = currentAssignments.length;
    
    // Check if this employee is already assigned to this position
    const isAlreadyAssigned = currentAssignments.some(
      (item) => item.employeeId === employeeId
    );
    
    // If employee is already assigned, no conflict
    if (isAlreadyAssigned) {
      return { hasConflict: false, conflictingEmployee: null };
    }
    
    // If we've reached the maximum capacity, it's a conflict
    if (currentAssignmentCount >= totalPositions) {
      const conflictingEmployee = shiftRoster.find((e) => e._id === currentAssignments[0]?.employeeId);
      return { 
        hasConflict: true, 
        conflictingEmployee: conflictingEmployee?.fullName || 'Unknown Employee',
        reason: 'capacity_full'
      };
    }
    
    return { hasConflict: false, conflictingEmployee: null };
  };

  // Helper function to check if position assignment is allowed
  const canAssignToPosition = (positionName, date, dayName, currentEmployeeId = null) => {
    const { available, assigned } = getPositionAvailabilityForDate(positionName, date, dayName);
    
    // If this employee is already assigned to this position on this date, allow it
    if (currentEmployeeId) {
      const dateKey = moment(date).format("YYYY-MM-DD");
      const roster = getValues(`shifts[${selectedShiftIndex}].defaultSchedule.${dayName}.roster`) || [];
      
      if (Array.isArray(roster) && roster.length > 0) {
        const existingAssignment = roster.find(
          (item) => item.employeeId === currentEmployeeId && 
                     item.date === dateKey && 
                     item.assignedPosition === positionName
        );
        if (existingAssignment) {
          return true; // Allow if employee is already assigned to this position
        }
      }
    }
    
    return available > 0;
  };

  // Set assignedPosition in the correct roster entry
  const handlePositionSelect = (positionName) => {
    if (!selectedEmployeeForPosition || !selectedDateForPosition || !selectedDayForPosition) return;
    
    const dayName = selectedDayForPosition;
    const currentPosition = getEmployeePositionForDay(selectedEmployeeForPosition._id, selectedDateForPosition, dayName);
    
    // If clearing position (positionName is empty or null)
    if (!positionName) {
      const roster = getValues(`shifts[${selectedShiftIndex}].defaultSchedule.${dayName}.roster`) || [];
      const updatedRoster = Array.isArray(roster) ? [...roster] : [];
      
      if (assignToAllWeek) {
        // Clear position from all weeks within shift date range
        if (shiftStartDate && shiftEndDate) {
          const start = moment(shiftStartDate);
          const end = moment(shiftEndDate);
          
          for (let m = moment(start); m.isSameOrBefore(end, "day"); m.add(1, "days")) {
            if (m.format("dddd").toLowerCase() === dayName) {
              const weekDateKey = m.format("YYYY-MM-DD");
              const existingIndex = updatedRoster.findIndex(
                (item) => item.employeeId === selectedEmployeeForPosition._id && item.date === weekDateKey
              );
              
              if (existingIndex !== -1) {
                // Clear the position
                updatedRoster[existingIndex] = {
                  ...updatedRoster[existingIndex],
                  assignedPosition: ""
                };
              }
            }
          }
        }
      } else {
        // Clear position only from the selected date
        const dateKey = moment(selectedDateForPosition).format("YYYY-MM-DD");
        const existingIndex = updatedRoster.findIndex(
          (item) => item.employeeId === selectedEmployeeForPosition._id && item.date === dateKey
        );
        
        if (existingIndex !== -1) {
          // Clear the position
          updatedRoster[existingIndex] = {
            ...updatedRoster[existingIndex],
            assignedPosition: ""
          };
        }
      }
      
      setValue(`shifts[${selectedShiftIndex}].defaultSchedule.${dayName}.roster`, updatedRoster, { shouldDirty: true });
      handlePositionClose();
      return;
    }

    // If changing to a different position
    if (positionName !== currentPosition) {
      // First check for position conflicts (different employees with same position on same day)
      const conflictCheck = checkPositionConflict(
        positionName, 
        selectedDateForPosition, 
        dayName, 
        selectedEmployeeForPosition._id
      );
      
      if (conflictCheck.hasConflict) {
        const { total, assigned } = getPositionAvailabilityForDate(
          positionName, 
          selectedDateForPosition, 
          dayName
        );
        setPositionError(`Cannot assign ${selectedEmployeeForPosition.fullName} to ${positionName}. Position is at maximum capacity (${assigned}/${total} assigned).`);
        return;
      }
      
      // Then check general availability
      const canAssign = canAssignToPosition(
        positionName, 
        selectedDateForPosition, 
        dayName, 
        selectedEmployeeForPosition._id
      );
      
      if (!canAssign) {
        const { total, assigned } = getPositionAvailabilityForDate(
          positionName, 
          selectedDateForPosition, 
          dayName
        );
        setPositionError(`Cannot assign to ${positionName}. All ${total} positions are already filled (${assigned}/${total} assigned).`);
        return;
      }
    }

    // Clear any previous errors
    setPositionError("");

    const roster = getValues(`shifts[${selectedShiftIndex}].defaultSchedule.${dayName}.roster`) || [];
    const updatedRoster = Array.isArray(roster) ? [...roster] : [];
    
    if (assignToAllWeek) {
      // Apply position to all weeks within shift date range
      if (shiftStartDate && shiftEndDate) {
        const start = moment(shiftStartDate);
        const end = moment(shiftEndDate);
        
        for (let m = moment(start); m.isSameOrBefore(end, "day"); m.add(1, "days")) {
          if (m.format("dddd").toLowerCase() === dayName) {
            const weekDateKey = m.format("YYYY-MM-DD");
            const existingIndex = updatedRoster.findIndex(
              (item) => item.employeeId === selectedEmployeeForPosition._id && item.date === weekDateKey
            );
            
            if (existingIndex !== -1) {
              // Update existing entry
              updatedRoster[existingIndex] = {
                ...updatedRoster[existingIndex],
                assignedPosition: positionName
              };
            } else {
              // Add new entry
              updatedRoster.push({
                employeeId: selectedEmployeeForPosition._id,
                date: weekDateKey,
                assignedPosition: positionName
              });
            }
          }
        }
      }
    } else {
      // Apply position only to the selected date
      const dateKey = moment(selectedDateForPosition).format("YYYY-MM-DD");
      const existingIndex = updatedRoster.findIndex(
        (item) => item.employeeId === selectedEmployeeForPosition._id && item.date === dateKey
      );
      
      if (existingIndex !== -1) {
        // Update existing entry
        updatedRoster[existingIndex] = {
          ...updatedRoster[existingIndex],
          assignedPosition: positionName
        };
      } else {
        // Add new entry
        updatedRoster.push({
          employeeId: selectedEmployeeForPosition._id,
          date: dateKey,
          assignedPosition: positionName
        });
      }
    }
    
    setValue(`shifts[${selectedShiftIndex}].defaultSchedule.${dayName}.roster`, updatedRoster, { shouldDirty: true });
    handlePositionClose();
  };

  // Navigate to previous/next week - optimized
  const navigateWeek = useCallback((direction) => {
  
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(currentWeekStart.getDate() + (direction * 7));
    
    
    // Check if the new week is within shift date range
    if (shiftStartDate && shiftEndDate) {
      const shiftStart = moment(shiftStartDate);
      const shiftEnd = moment(shiftEndDate);
      const newWeekEnd = moment(newWeekStart).add(6, 'days');
    
      // Only allow navigation if the new week overlaps with shift date range
      if (newWeekEnd.isBefore(shiftStart) || moment(newWeekStart).isAfter(shiftEnd)) {
        return; // Don't navigate outside shift range
      }
    }
    
    setCurrentWeekStart(newWeekStart);
  }, [currentWeekStart, shiftStartDate, shiftEndDate]);

  // Columns for DataGrid - memoized for performance
  const columns = useMemo(() => [
    {
      field: "fullName",
      headerName: "Applicants",
      flex: 1,
      renderHeader: () => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="h6">Applicants</Typography>
        </Box>
      ),
    },
    ...validDays.map(([day, { start, end }]) => {
      const dayDate = weekDates.find(date => moment(date).format("dddd").toLowerCase() === day);
      const dateKey = dayDate ? moment(dayDate).format("YYYY-MM-DD") : "";
      const isAvailable = isDayAvailable(day);
            
      return {
        field: `${day}_${dateKey}`,
        headerName: day,
        flex: 1,
        sortable: false,
        renderHeader: () => (
          <Box 
            sx={{ 
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              textAlign: 'center',
              opacity: isAvailable ? 1 : 0.5,
              transition: 'opacity 0.3s ease-in-out'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {capitalizeFirstLetter(day)}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {moment(start).format("hh:mm A")} - {moment(end).format("hh:mm A")}
            </Typography>
            {dayDate && (
              <Typography variant="body2" sx={{ 
                fontSize: "0.8rem", 
                color: "text.secondary",
                fontWeight: 500,
                backgroundColor: "rgba(25, 118, 210, 0.1)",
                borderRadius: 1,
                px: 1,
                py: 0.5
              }}>
                {moment(dayDate).format("MMM DD")}
              </Typography>
            )}
            {!isAvailable && (
              <Typography variant="caption" sx={{ color: "text.disabled", mt: 0.5 }}>
                Not available
              </Typography>
            )}
          </Box>
        ),
        renderCell: (params) => {
          const isChecked = Boolean(params.value);
          const employee = shiftRoster.find(emp => emp._id === params.row.id);
          const currentPosition = getEmployeePositionForDay(params.row.id, dayDate, day);
          
          return (
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center',
                width: '100%',
                height: '100%',
                opacity: isAvailable ? 1 : 0.5,
                transition: 'all 0.2s ease-in-out',
                position: 'relative',
                p: 1
              }}
            >
              <Checkbox
                checked={isChecked}
                disabled={!isAvailable}
                onChange={(e) => handleCheckboxChange(day, dayDate, params.row.id, e.target.checked)}
                sx={{
                  transition: 'all 0.15s ease-in-out',
                }}
              />
              
              {isChecked && isAvailable && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 0.5,
                  mt: 0.5
                }}>
                  {currentPosition ? (
                    <Typography
                      variant="caption"
                      sx={{
                        cursor: 'pointer',
                        color: '#1976d2',
                        textDecoration: 'underline',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        '&:hover': {
                          color: '#1565c0',
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePositionButtonClick(e, employee, dayDate, day);
                      }}
                    >
                      {currentPosition}
                    </Typography>
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{
                        cursor: 'pointer',
                        color: '#666',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        '&:hover': {
                          color: '#1976d2',
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePositionButtonClick(e, employee, dayDate, day);
                      }}
                    >
                      Add Position
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          );
        },
      };
    }),
  ], [validDays, weekDates, isDayAvailable, handleCheckboxChange, capitalizeFirstLetter, shiftRoster, getEmployeePositionForDay, handlePositionButtonClick]);

  const header = (
    <Box display="flex" sx={{ mt: 2, mb: 1, ml: 2 }} justifyContent="space-between" alignItems="center">
      <Stack direction="row" alignItems="center">
        <Typography variant="h5" color="dark">
          Weekly Schedule Configuration for
        </Typography>
        &nbsp;
        <Typography variant="h5" color="info.main">
          {`${getValues(`shifts[${selectedShiftIndex}].shiftName`)} (${moment(
            getValues(`shifts[${selectedShiftIndex}].shiftStartDate`)
          ).format("MM/DD/YYYY")} - ${moment(
            getValues(`shifts[${selectedShiftIndex}].shiftEndDate`)
          ).format("MM/DD/YYYY")})`}
        </Typography>
      </Stack>
      
      <Stack direction="row" alignItems="center" spacing={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={assignToAllWeek}
              onChange={(e) => setAssignToAllWeek(e.target.checked)}
            />
          }
          label={
            <Tooltip title="When checked, selecting a day will assign to all weeks from shift start to end date">
              <Typography variant="body2">Affective for all Weeks</Typography>
            </Tooltip>
          }
        />
        
        <Box display="flex" alignItems="center" spacing={1}>
          <IconButton
            onClick={() => navigateWeek(-1)}
            size="small"
            sx={{
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2" sx={{ mx: 1, minWidth: 150, textAlign: "center" }}>
            {weekDates.length > 0 ? (
              `${moment(weekDates[0]).format("MMM DD")} - ${moment(weekDates[weekDates.length - 1]).format("MMM DD")}`
            ) : (
              "No dates in range"
            )}
          </Typography>
          <IconButton
            onClick={() => navigateWeek(1)}
            size="small"
            sx={{
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
        
        <IconButton
          onClick={() => {
            setWeeklyScheduleModalOpen(false);
          }}
        >
          <Cancel />
        </IconButton>
      </Stack>
    </Box>
  );

  const modalBody = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper style={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={5}
            disableColumnMenu
            disableSelectionOnClick
            headerHeight={120}
            rowHeight={80}
            sx={{
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#f5f5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiDataGrid-columnHeaderTitleContainer": {
  justifyContent: "center",
},
              
            }}
          />
        </Paper>
      </Grid>
      <Grid item xs={9} />
      <Grid item sm={3}>
        <Grid container spacing={3} display="flex">
          <Grid item sm={6}>
            <Button
              type="button"
              variant="contained"
              color="error"
              style={{ color: "error" }}
              fullWidth
              onClick={() => setWeeklyScheduleModalOpen(false)}
            >
              Close
            </Button>
          </Grid>
          <Grid item sm={6}>
            <Button
              type="button"
              variant="contained"
              endIcon={<SaveIcon />}
              style={{ color: "white" }}
              sx={{ padding: "0.5rem 1rem" }}
              fullWidth
              onClick={(e) => {
                e.stopPropagation();
                saveChanges();
                setWeeklyScheduleModalOpen(false);
              }}
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  // If there are no valid days, hide the DataGrid
  if (validDays.length === 0) {
    return null;
  }

  return (
    <>
      <GenericModal
        open={isWeeklyScheduleModalOpen}
        setOpen={setWeeklyScheduleModalOpen}
        header={header}
        body={modalBody}
        width="80%"
        overflow="auto"
      />
      
      {/* Position Selection Popup */}
      <Popover
        open={Boolean(positionPopupAnchor)}
        anchorEl={positionPopupAnchor}
        onClose={handlePositionClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            border: "1px solid #e0e0e0",
            backgroundColor: "#fff",
            opacity: 1,
            minWidth: 280,
            p: 0,
          },
        }}
      >
        {console.log("Popover render - open:", Boolean(positionPopupAnchor), "anchor:", positionPopupAnchor)}
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Assign Position
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            {selectedEmployeeForPosition?.fullName} - {selectedDateForPosition ? moment(selectedDateForPosition).format("MMM DD, YYYY") : ""}
          </Typography>
          
          <Autocomplete
            options={getShiftPositions().map((pos) => pos.positionName)}
            value={getEmployeePositionForDay(selectedEmployeeForPosition?._id, selectedDateForPosition, selectedDayForPosition)}
            onChange={(e, newValue) => {
              setSelectedPosition(newValue || "");
              handlePositionSelect(newValue);
            }}
            onOpen={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            getOptionLabel={(option) => option || ""}
            renderOption={(props, option) => {
              const pos = getShiftPositions().find((p) => p.positionName === option);
              const availability = getPositionAvailabilityForDate(
                option, 
                selectedDateForPosition, 
                selectedDayForPosition
              );
              const isAvailable = canAssignToPosition(
                option, 
                selectedDateForPosition, 
                selectedDayForPosition, 
                selectedEmployeeForPosition?._id
              );
              const conflictCheck = checkPositionConflict(
                option, 
                selectedDateForPosition, 
                selectedDayForPosition, 
                selectedEmployeeForPosition?._id
              );
              
              return (
                <li {...props} key={option} style={{ 
                  opacity: isAvailable && !conflictCheck.hasConflict ? 1 : 0.5,
                  cursor: isAvailable && !conflictCheck.hasConflict ? 'pointer' : 'not-allowed'
                }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, color: isAvailable && !conflictCheck.hasConflict ? 'inherit' : '#999' }}>
                        {option}
                      </span>
                      <Chip
                        label={`${availability.available}/${availability.total}`}
                        size="small"
                        sx={{
                          fontSize: '0.7rem',
                          height: 20,
                          backgroundColor: availability.available > 0 ? '#e8f5e8' : '#ffebee',
                          color: availability.available > 0 ? '#2e7d32' : '#c62828',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    {pos && (
                      <span style={{ fontSize: '0.8em', color: '#888' }}>
                        Pay: ${pos.payRate} &nbsp;|&nbsp; Bill: ${pos.billRate}
                      </span>
                    )}
                    {conflictCheck.hasConflict && (
                      <span style={{ fontSize: '0.75em', color: '#f44336', fontWeight: 500 }}>
                        Position at maximum capacity ({availability.assigned}/{availability.total})
                      </span>
                    )}
                    {!isAvailable && availability.total > 0 && !conflictCheck.hasConflict && (
                      <span style={{ fontSize: '0.75em', color: '#f44336', fontWeight: 500 }}>
                        All positions filled ({availability.assigned}/{availability.total})
                      </span>
                    )}
                  </Box>
                </li>
              );
            }}
            filterOptions={(options, { inputValue }) => {
              // Show all positions but mark unavailable ones as disabled
              return options;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Position"
                size="small"
                placeholder="Choose a position..."
                onClick={(e) => e.stopPropagation()}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                  },
                }}
              />
            )}
            sx={{ mb: 2 }}
            disableClearable
            fullWidth
          />
          
          {/* Error Display */}
          {positionError && (
            <Typography
              variant="body2"
              color="error"
              sx={{ 
                mb: 2, 
                p: 1, 
                backgroundColor: "#ffebee", 
                borderRadius: 1,
                border: "1px solid #f44336"
              }}
            >
              {positionError}
            </Typography>
          )}
          
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            {getEmployeePositionForDay(selectedEmployeeForPosition?._id, selectedDateForPosition, selectedDayForPosition) && (
              <Button
                variant="text"
                color="error"
                size="small"
                onClick={() => handlePositionSelect("")}
                sx={{ 
                  textTransform: "none",
                  color: "#f44336",
                  "&:hover": {
                    backgroundColor: "#ffebee",
                  }
                }}
              >
                Clear Position
              </Button>
            )}
            <Button
              size="small"
              onClick={handlePositionClose}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default WeeklyScheduleConfigurationModal;
